// Block-2-Karte „Schäden" (AP-S, DD6/DD8): KPI „N offene Schäden" + Top-5
// offene Schäden je gewähltem Scope, jeweils mit dem zugehörigen Kunden (DD6).
// Pills NUR „Meine"/„Team" (DD8) — kein „Alle". Reuse des bestehenden
// `GET /damage?status=offen`-Endpunkts (kein neuer Backend-Endpunkt).
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { ShieldError20Regular, ChevronRight16Regular } from "@fluentui/react-icons";
import { useAuth } from "../../context/AuthContext";
import { dashboardService, type DashScope, type DashboardDamage } from "../../services/dashboardService";
import { REFINED } from "../risikoanalyseStep/tokens";
import { DashCard, EmptyHint, FilterPill, deDate } from "./DashboardShared";

const LIMIT = 5;

export const SchaedenCard: React.FC = () => {
    const navigate = useNavigate();
    const { user, teammates, teams } = useAuth();
    const [scope, setScope] = useState<DashScope>("own");
    const [items, setItems] = useState<DashboardDamage[]>([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const ctx = useMemo(
        () => ({
            userId: user?.id ?? null,
            teammates: teammates ?? [],
            teamIds: (teams ?? []).map((t) => t.id),
        }),
        [user?.id, teammates, teams],
    );

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        dashboardService
            .getOpenDamages(scope, ctx, LIMIT)
            .then((res) => {
                if (cancelled) return;
                setItems(res.items);
                setCount(res.count);
            })
            .catch(() => {
                if (cancelled) return;
                setItems([]);
                setCount(0);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [scope, ctx]);

    const goAll = () => navigate("/damages");
    const goDamage = (id: number) => navigate(`/schaeden/${id}`);

    return (
        <DashCard
            title="Schäden"
            icon={<ShieldError20Regular />}
            right={
                <button
                    type="button"
                    onClick={goAll}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        color: REFINED.aub,
                        fontFamily: REFINED.fontUI,
                        fontSize: "0.88rem",
                        fontWeight: 700,
                        padding: 0,
                    }}
                >
                    Alle anzeigen <ChevronRight16Regular />
                </button>
            }
        >
            {/* Pills NUR Meine/Team (DD8) */}
            <div style={{ display: "flex", gap: 8, paddingTop: 8, paddingBottom: 4 }}>
                <FilterPill label="Meine" count={scope === "own" ? count : 0} active={scope === "own"} onClick={() => setScope("own")} />
                <FilterPill label="Team" count={scope === "team" ? count : 0} active={scope === "team"} onClick={() => setScope("team")} />
            </div>

            {/* KPI: N offene Schäden */}
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "6px 6px 10px" }}>
                <span style={{ fontSize: "2rem", fontWeight: 800, color: REFINED.ink, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                    {count}
                </span>
                <span style={{ fontSize: "0.92rem", color: REFINED.ink2, fontWeight: 600 }}>
                    {count === 1 ? "offener Schaden" : "offene Schäden"}
                </span>
            </div>

            {/* Top-5-Liste: Schadennr/Titel + Kunde (DD6) + Datum */}
            {loading ? (
                <EmptyHint>Lade Schäden…</EmptyHint>
            ) : items.length === 0 ? (
                <EmptyHint>{scope === "own" ? "Keine offenen Schäden." : "Keine offenen Schäden im Team."}</EmptyHint>
            ) : (
                <div>
                    {items.map((d, idx) => (
                        <DamageRow key={d.id} damage={d} border={idx > 0} onClick={() => goDamage(d.id)} />
                    ))}
                </div>
            )}
        </DashCard>
    );
};

const DamageRow: React.FC<{ damage: DashboardDamage; border: boolean; onClick: () => void }> = ({ damage, border, onClick }) => {
    const headline = damage.title || damage.damageNbr || "Schaden";
    return (
        <div
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onClick();
                }
            }}
            style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 6px",
                borderTop: border ? `1px solid ${REFINED.line}` : "none",
                cursor: "pointer",
            }}
        >
            <span style={{ width: 9, height: 9, borderRadius: 999, backgroundColor: REFINED.aub, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: "0.95rem", color: REFINED.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {headline}
                </div>
                <div style={{ fontSize: "0.83rem", color: REFINED.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {damage.contactName || "—"}
                    {damage.damageNbr && headline !== damage.damageNbr ? ` · ${damage.damageNbr}` : ""}
                </div>
            </div>
            <span style={{ flexShrink: 0, fontVariantNumeric: "tabular-nums", color: REFINED.ink3, fontWeight: 700, fontSize: "0.82rem" }}>
                {deDate(damage.damageDate)}
            </span>
        </div>
    );
};

export default SchaedenCard;
